import { apiClient, HttpError, streamText } from './apiClient';
import type { RelanceEmailDto, SendEmailRequest, SendEmailResponse } from '../types/customerFollowUp/email';
import type { EmailTone } from '../types/customerFollowUp/enums';
import type { TemplateLanguage } from '../types/customEmailTemplate';

// Mirrors EmailService.AiFallbackMarker (backend) — never text the model generated.
const AI_FALLBACK_MARKER = '[AI_FALLBACK]';

export const emailsService = {
  // 404 = no saved draft yet → null, so the caller loads the default template instead of erroring.
  getDraft: async (followUpId: string): Promise<RelanceEmailDto | null> => {
    try {
      return await apiClient.get<RelanceEmailDto>(
        `/relances/${followUpId}/relance-email/body`
      );
    } catch (e) {
      if (e instanceof HttpError && e.status === 404) return null;
      throw e;
    }
  },

  streamBodyRefresh: async function* (
    followUpId: string, tone: EmailTone, noAi = false, signal?: AbortSignal, onAiFallback?: () => void
  ): AsyncGenerator<string> {
    for await (const chunk of streamText(`/relances/${followUpId}/relance-email/stream?tone=${tone}${noAi ? '&noAi=true' : ''}`, signal)) {
      if (chunk === AI_FALLBACK_MARKER) {
        onAiFallback?.();
        continue;
      }
      yield chunk;
    }
  },

  getDraftTemplate: (followUpId: string, tone: EmailTone) =>
    apiClient.get<RelanceEmailDto>(
      `/relances/${followUpId}/relance-email/template?tone=${tone}`
    ),

  // 404 = the company has no custom template → null.
  getCustomDraft: async (followUpId: string, language?: TemplateLanguage): Promise<RelanceEmailDto | null> => {
    try {
      return await apiClient.get<RelanceEmailDto>(
        `/relances/${followUpId}/relance-email/custom${language ? `?language=${language}` : ''}`
      );
    } catch (e) {
      if (e instanceof HttpError && e.status === 404) return null;
      throw e;
    }
  },

  upsertDraft: (followUpId: string, draft: RelanceEmailDto) =>
    apiClient.post<RelanceEmailDto>(
      `/relances/${followUpId}/relance-email`,
      draft
    ),

  sendEmail: (followUpId: string, request: SendEmailRequest, manualFiles: File[] = []) => {
    const formData = new FormData();
    if (request.draftId) formData.append('draftId', request.draftId);
    if (request.draft) formData.append('draftJson', JSON.stringify(request.draft));
    if (request.autoAttachmentIds) {
      // Empty list ≠ "not provided": post a single empty entry so the server treats
      // it as "client cleared every auto-attachment" instead of falling back to all rows.
      if (request.autoAttachmentIds.length === 0) {
        formData.append('autoAttachmentIds', '');
      } else {
        request.autoAttachmentIds.forEach(id => formData.append('autoAttachmentIds', id));
      }
    }
    if (request.includePaymentLinks !== undefined) {
      formData.append('includePaymentLinks', String(request.includePaymentLinks));
    }
    if (request.draftEdited !== undefined) {
      formData.append('draftEdited', String(request.draftEdited));
    }
    if (request.aiSelectedTone !== undefined) {
      formData.append('aiSelectedTone', request.aiSelectedTone);
    }
    manualFiles.forEach(file => formData.append('manualAttachments', file, file.name));
    return apiClient.postFormData<SendEmailResponse>(
      `/relances/${followUpId}/relance-email/send`,
      formData
    );
  },
};
